import * as Yup from "yup";
import { DateTime } from "luxon";

import { createForm } from "../../forms";
import { scheduledPaymentBaseSchema } from "../../modules/achPayments/ACHPayment.form";

import { basePaymentRowSchema, baseBulkFormSchema } from "./bulkBase";

import type { TypeSafeReactLocalization } from "../../../composites";
import type { ACHPaymentFormBaseType } from "../../modules/achPayments/ACHPayment.form";
import type { BasePaymentRowType } from "./bulkBase";

export type BulkPaymentRowType = BasePaymentRowType & {
  scheduledDate: string;
  speedStatus?: "instant" | "sameDay" | "standard" | "";
  speedStatusWithExpediteOption?: "instant" | "sameDay" | "standard" | "";
  // Template mode only. The `payment_data` row uuid this form row came from,
  // so overrides are keyed by identity instead of array position.
  templateRowId?: string;
};

export type BulkPaymentFormType = ACHPaymentFormBaseType & {
  title: string;
  payments: BulkPaymentRowType[];
  groupPosting: boolean;
  description?: string;
  chosenExpediteOption?: "fastest" | "sameDay" | "fedNow" | "";
  totalExpediteFees?: number;
};

export type BulkPaymentTransactionType = BulkPaymentRowType["transactionType"];

const permittedTransactionTypeSchema = (
  l10n: TypeSafeReactLocalization,
  permittedTransactionTypes: BulkPaymentTransactionType[],
) =>
  Yup.mixed<BulkPaymentTransactionType>().test(
    "permitted-transaction-type",
    "",
    function (value) {
      if (!value || permittedTransactionTypes.includes(value)) {
        return true;
      }
      return this.createError({
        message: l10n.getString("bulk-ach-form-error-transaction-type", {
          action: value,
        }),
      });
    },
  );

const paymentSchema = (
  l10n: TypeSafeReactLocalization,
  permittedTransactionTypes?: BulkPaymentTransactionType[],
) =>
  basePaymentRowSchema(l10n).concat(
    Yup.object().shape({
      scheduledDate: Yup.date()
        .typeError(l10n.getString("bulk-ach-form-error-date"))
        .required(l10n.getString("bulk-ach-form-error-date")),
      ...(permittedTransactionTypes && {
        transactionType: permittedTransactionTypeSchema(
          l10n,
          permittedTransactionTypes,
        ),
      }),
    }),
  );

export const bulkPaymentSchema = (l10n: TypeSafeReactLocalization) =>
  baseBulkFormSchema(l10n).concat(scheduledPaymentBaseSchema(l10n));

export const paymentsSchema = (
  l10n: TypeSafeReactLocalization,
  permittedTransactionTypes?: BulkPaymentTransactionType[],
) =>
  Yup.object().shape({
    payments: Yup.array()
      .of(paymentSchema(l10n, permittedTransactionTypes))
      .min(1, l10n.getString("bulk-ach-form-error-payment")),
  });

export const hasPaymentBeforeCutoff = (
  paymentDate: string,
  cutoffDate: string,
): boolean => {
  const scheduled = DateTime.fromFormat(paymentDate, "MM/dd/yyyy");
  const cutoff = DateTime.fromFormat(cutoffDate, "MM/dd/yyyy");
  return scheduled < cutoff; // luxon DateTime supports direct comparison
};

export const getUpdatedBulkPaymentSchema =
  (
    achNextCutoffDate: string,
    fedNowOnly: boolean,
    permittedTransactionTypes?: BulkPaymentTransactionType[],
  ) =>
  (l10n: TypeSafeReactLocalization) => {
    const { getString } = l10n;
    const updatedPaymentSchema = paymentSchema(
      l10n,
      permittedTransactionTypes,
    ).concat(
      Yup.object().shape({
        scheduledDate: Yup.date()
          .typeError(getString("bulk-ach-form-error-date"))
          .required(getString("bulk-ach-form-error-date"))
          .test("expedite-validation", "", function (value) {
            if (!value) return true;

            const { chosenExpediteOption } = this.options.context || {};
            const { speedStatus, speedStatusWithExpediteOption } = this.parent;

            const dateIsBeforeCutoff = hasPaymentBeforeCutoff(
              DateTime.fromJSDate(value).toFormat("MM/dd/yyyy"),
              achNextCutoffDate,
            );

            // instant payments that are scheduled before cutoff that need to be expedited
            if (
              dateIsBeforeCutoff &&
              speedStatus === "instant" &&
              speedStatusWithExpediteOption !== "instant" &&
              (fedNowOnly
                ? chosenExpediteOption !== "fedNow"
                : chosenExpediteOption !== "fastest")
            ) {
              return this.createError({
                message: l10n.getString("bulk-ach-form-error-instant"),
              });
            }

            // same day ACH payments (both send and collect) that are scheduled before cutoff that need to be rescheduled to another day
            if (
              dateIsBeforeCutoff &&
              (speedStatus === "sameDay" || speedStatus === "standard")
            ) {
              return this.createError({
                message: l10n.getString("bulk-ach-form-error-same-day"),
              });
            }

            return true;
          }),
      }),
    );

    return bulkPaymentSchema(l10n).concat(
      Yup.object().shape({
        // override the original payments schema with updated "scheduledDate" validations
        payments: Yup.array()
          .of(updatedPaymentSchema)
          .min(1, l10n.getString("bulk-ach-form-error-payment")),
      }),
    );
  };

export const BulkRowInitialValue = {
  scheduledDate: "",
  recipient: undefined,
  description: "",
  amount: 0,
  transactionType: "send" as const,
};

export const initialValues: BulkPaymentFormType = {
  title: "",
  fromAccount: "",
  secCode: "",
  memo: "",
  entryDesc: "",
  payments: [{ ...BulkRowInitialValue }],
  groupPosting: false,
  chosenExpediteOption: "",
};

export const BulkPaymentForm = createForm({
  initialValues,
  translateValidationSchema: bulkPaymentSchema,
});
